import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import NavTabs from '../components/NavTabs';
import { useAuth } from '../context/AuthContext';
import { getAccount, cancelSubscription, requestFacilityLink } from '../api';
import FacilitySearchSelect from '../components/FacilitySearchSelect';

const ACCOUNT_LABELS = {
  guest:            { label: 'Guest',                      color: 'bg-gray-100 text-gray-600' },
  comp_individual:  { label: 'Complimentary Individual',   color: 'bg-purple-100 text-purple-700' },
  comp_org:         { label: 'Complimentary Organization', color: 'bg-purple-100 text-purple-700' },
  subscriber_individual: { label: 'Individual Subscriber',      color: 'bg-green-100 text-green-700' },
  subscriber_org:        { label: 'Organization Subscriber',    color: 'bg-green-100 text-green-700' },
  admin:            { label: 'Administrator',              color: 'bg-blue-100 text-blue-700' },
};

function AccountBadge({ accountType }) {
  const { label, color } = ACCOUNT_LABELS[accountType] || ACCOUNT_LABELS.guest;
  return (
    <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${color}`}>
      {label}
    </span>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 flex-shrink-0 w-36">{label}</span>
      <span className="text-sm text-gray-900 text-right">{value}</span>
    </div>
  );
}

export default function Account() {
  const { user, logout, updateUser, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [accountData, setAccountData] = useState(user);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError]     = useState('');
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linkFacility, setLinkFacility]   = useState(null);
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkSuccess, setLinkSuccess]     = useState('');
  const [linkError, setLinkError]         = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/account' } });
      return;
    }
    getAccount()
      .then((data) => {
        setAccountData(data);
        updateUser(data);
      })
      .catch(() => setAccountData(user))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCancel = async () => {
    if (!cancelConfirm) { setCancelConfirm(true); return; }
    setCancelError('');
    setCancelLoading(true);
    try {
      await cancelSubscription();
      const updated = await getAccount();
      setAccountData(updated);
      updateUser(updated);
      setCancelConfirm(false);
    } catch (err) {
      setCancelError(err?.response?.data?.error || 'Could not cancel. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const acct        = accountData || user;
  const isSubscriber  = acct?.account_type === 'subscriber_individual' || acct?.account_type === 'subscriber_org';
  const isComp        = acct?.account_type === 'comp_individual' || acct?.account_type === 'comp_org';
  const isOrgAccount  = ['comp_org', 'subscriber_org'].includes(acct?.account_type);
  const isGuest      = !acct || acct.account_type === 'guest';
  const fullName     = [acct?.first_name, acct?.last_name].filter(Boolean).join(' ') || '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header right={null} />
      <NavTabs />

      <main className="max-w-2xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">My Account</h1>

        {loading ? (
          <div className="flex justify-center py-16">
            <svg className="animate-spin w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Profile */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-800">Profile</h2>
                <AccountBadge accountType={acct?.account_type} />
              </div>
              <InfoRow label="Name"           value={fullName} />
              <InfoRow label="Email"          value={acct?.email} />
              <InfoRow label="Organization"   value={acct?.organization_name} />
              <InfoRow label="Role"           value={acct?.role_title} />
              <InfoRow label="Primary county" value={acct?.primary_county ? `${acct.primary_county} County` : null} />
            </div>

            {/* Subscription */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-5">
              <h2 className="text-base font-semibold text-gray-800 mb-4">Subscription</h2>

              {isGuest && (
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm text-gray-500">
                    Guest plan — county search and facility names only.
                  </p>
                  <Link to="/subscribe"
                    className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
                    Subscribe
                  </Link>
                </div>
              )}

              {isComp && (
                <div className="space-y-1.5">
                  <p className="text-sm text-gray-700">
                    Complimentary full access
                    {acct?.comp_expiration_on && acct?.comp_expires_at
                      ? ` — expires ${new Date(acct.comp_expires_at).toLocaleDateString()}`
                      : ' — no expiration set'}
                  </p>
                </div>
              )}

              {isSubscriber && (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {acct.account_type === 'subscriber_org' ? 'Organization Plan — $25/month' : 'Individual Plan — $9.99/month'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Status:{' '}
                      <span className={acct.subscription_status === 'active' ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                        {acct.subscription_status || 'active'}
                      </span>
                    </p>
                  </div>

                  {cancelError && <p className="text-sm text-red-600">{cancelError}</p>}

                  {cancelConfirm ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-sm text-gray-600 flex-1 min-w-0">
                        Are you sure? You'll lose full access at the end of your billing period.
                      </p>
                      <button onClick={() => setCancelConfirm(false)}
                        className="text-sm text-gray-500 hover:text-gray-700 underline">
                        Keep plan
                      </button>
                      <button onClick={handleCancel} disabled={cancelLoading}
                        className="text-sm text-red-600 hover:text-red-800 font-medium underline">
                        {cancelLoading ? 'Cancelling…' : 'Yes, cancel'}
                      </button>
                    </div>
                  ) : (
                    <button onClick={handleCancel}
                      className="text-sm text-gray-500 hover:text-red-600 underline underline-offset-2 transition-colors">
                      Cancel subscription
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Facility listings — admin can edit any facility */}
            {isAdmin && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-5">
                <h2 className="text-base font-semibold text-gray-800 mb-4">Facility Listings</h2>
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm text-gray-600">
                    Edit any facility's profile, bed availability, and API keys.
                  </p>
                  <Link
                    to="/my-facility"
                    className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                  >
                    Edit a listing →
                  </Link>
                </div>
              </div>
            )}

            {/* Facility Listing — org accounts only */}
            {isOrgAccount && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-5">
                <h2 className="text-base font-semibold text-gray-800 mb-4">Your Facility Listing</h2>

                {acct?.linked_facility_id ? (
                  /* Approved — show edit link */
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-gray-600">
                      Your account is linked to a facility listing.
                    </p>
                    <Link
                      to="/my-facility"
                      className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                    >
                      Edit your listing →
                    </Link>
                  </div>
                ) : acct?.pending_facility_id ? (
                  /* Pending approval */
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                    <p className="text-sm font-medium text-amber-800">Link request pending approval</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      Your request to link your account to a facility listing is awaiting admin review.
                    </p>
                  </div>
                ) : (
                  /* Not linked — show search */
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">
                      Search for your facility to request a link. Once approved, you can edit and maintain your listing.
                    </p>
                    <FacilitySearchSelect
                      selected={linkFacility}
                      onSelect={setLinkFacility}
                      disabled={linkSubmitting}
                    />
                    {linkFacility && (
                      <button
                        onClick={async () => {
                          setLinkSubmitting(true);
                          setLinkError('');
                          try {
                            await requestFacilityLink(linkFacility.id);
                            setLinkSuccess('Link request submitted! An admin will review it shortly.');
                            setLinkFacility(null);
                            const updated = await getAccount();
                            setAccountData(updated);
                            updateUser(updated);
                          } catch (err) {
                            setLinkError(err?.response?.data?.error || 'Failed to submit request.');
                          } finally {
                            setLinkSubmitting(false);
                          }
                        }}
                        disabled={linkSubmitting}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {linkSubmitting ? 'Submitting…' : 'Request link'}
                      </button>
                    )}
                    {linkSuccess && <p className="text-sm text-green-700">{linkSuccess}</p>}
                    {linkError   && <p className="text-sm text-red-600">{linkError}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Sign out */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">Sign out</p>
                  <p className="text-xs text-gray-500 mt-0.5">You'll need to sign in again to access your account.</p>
                </div>
                <button onClick={handleLogout}
                  className="text-sm text-red-600 hover:text-red-800 font-medium border border-red-200 hover:border-red-300 px-4 py-2 rounded-lg transition-colors">
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
